import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import PSInput from '@/components/ui/PSInput';
import { isValidEmail, normalizeSocialLink } from '@/src/utils/validation';

/**
 * ── /components/ps-input ─────────────────────────────────────────────────
 *
 * PSInput across its full `type` surface — text, email, code, url — plus
 * disabled. Each `type` is a bundle of keyboard/autofill/autocorrect config
 * (`TYPE_PROPS` in the component), not just a label, so every block below
 * both shows the visual (border/error states) AND says what typing into it
 * actually feels like on a device. Error is render-only on PSInput itself —
 * every validating block here reproduces the real screen's validate-on-blur
 * / clear-on-focus rhythm (see the component's docstring) rather than
 * inventing a different one for the gallery.
 *
 * There's no `type="mobile"` demo — that's PSMobileInput, a separate
 * component (visibly-prefixed +91), not a PSInput type. See PSInput's own
 * docstring for why.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function PSInputPreview() {
  // text — mirrors auth/details.tsx's firstName/lastName gate: empty fails,
  // clear the error the moment they come back to fix it.
  const [textInput, setTextInput] = useState('');
  const [textError, setTextError] = useState<string | undefined>();

  // email — seeded with an invalid value AND its error already set, so the
  // error look (red border + message) is visible on load without having to
  // blur anything first. auth/email.tsx additionally trims the value on
  // blur before validating — reproduced here too.
  const [emailInput, setEmailInput] = useState('not-an-email');
  const [emailError, setEmailError] = useState<string | undefined>(
    'Enter a valid email address'
  );

  // code — memberships/index.tsx's coupon field: the SCREEN forces the typed
  // value uppercase in onChangeText (coupon codes are uppercase-only), which
  // is layered on top of TYPE_PROPS.code already shift-locking the keyboard
  // to caps. There's no local blur-validation here because real coupon
  // errors come back from the API (`isCouponCodeError`), not a client rule —
  // see the separate seeded-error block below for that look.
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState<string | undefined>();

  // url — profile/edit.tsx's social-link field (also SocialLinkSheet and
  // memberships/purchase/status.tsx's share-your-pass field): validated with
  // the same `normalizeSocialLink` those screens call, on blur.
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState<string | undefined>();

  return (
    <>
      <Head>
        <title>PSInput · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* PSInput's own root is `alignSelf="stretch"`, which needs a
            stretching ancestor to mean anything — this page's outer YStack is
            `items="flex-start"` (content-width), so every group below lives
            inside one shared stretch wrapper, the same shape every real
            call site's parent gives it for free. */}
          <YStack alignSelf="stretch" gap="$lg">
            {/* type="text" — default keyboard, sentence autoCapitalize. Focus
              the field to see the brand-purple border; blur it empty to see
              the error look. `autoFocus` (auth/details.tsx's firstName) and
              the `spellCheck={false}`/`autoCorrect={false}` overrides
              (details.tsx, profile/edit.tsx — plain names shouldn't get
              autocorrect suggestions) aren't demoed live here: autoFocus only
              fires from a `useFocusEffect` on screen-focus, which a gallery
              route full of sibling inputs has no single meaningful target
              for. */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                type=&quot;text&quot; — default keyboard, focus → purple border,
                blank blur → error (auth/details.tsx, profile/edit.tsx)
              </Text>
              <PSInput
                type="text"
                value={textInput}
                onChangeText={setTextInput}
                placeholder="First name"
                error={textError}
                onFocus={() => setTextError(undefined)}
                onBlur={() =>
                  setTextError(
                    textInput.trim() ? undefined : 'Enter your first name'
                  )
                }
              />
            </YStack>

            {/* type="email" — validates on blur, clears on focus, trims
              before checking (auth/email.tsx — the field also prefills from
              whatever's already on the session user, which is why trimming
              on blur matters: a pasted value with trailing whitespace
              shouldn't fail silently forever). Loads pre-seeded invalid so
              the error look is visible without interacting at all. */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                type=&quot;email&quot; — validates + trims on blur, clears on
                focus; seeded invalid so the error look shows on load
              </Text>
              <PSInput
                type="email"
                value={emailInput}
                onChangeText={setEmailInput}
                placeholder="you@example.com"
                error={emailError}
                onFocus={() => setEmailError(undefined)}
                onBlur={() => {
                  setEmailInput(emailInput.trim());
                  setEmailError(
                    isValidEmail(emailInput)
                      ? undefined
                      : 'Enter a valid email address'
                  );
                }}
              />
            </YStack>

            {/* type="code" — TYPE_PROPS bakes in the caps-lock keyboard and
              kills autofill/autocorrect/spellcheck (there's nothing in a
              coupon code any of those should touch); memberships/index.tsx
              layers its OWN uppercase transform on top in onChangeText so
              typed lowercase still lands as caps, and clears any stale error
              the moment the value changes (a new code invalidates the old
              verdict). Try typing lowercase below. */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                type=&quot;code&quot; — caps-lock keyboard, no
                autofill/autocorrect; screen force-uppercases as you type
                (memberships/index.tsx coupon field)
              </Text>
              <PSInput
                type="code"
                value={codeInput}
                onChangeText={(next) => {
                  setCodeInput(next.toUpperCase());
                  setCodeError(undefined);
                }}
                placeholder="Enter your code"
                error={codeError}
              />
            </YStack>
            {/* Coupon errors are server-driven (`isCouponCodeError`), not a
              local blur rule like the fields above — there's no client-side
              "this doesn't look like a code" check to trigger interactively,
              so this second block is a static stand-in for what the field
              looks like the moment `POST` comes back rejected. */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                type=&quot;code&quot; — the server-rejected look (coupon errors
                come from the API, not local validation)
              </Text>
              <PSInput
                type="code"
                value="EXPIRED20"
                onChangeText={() => {}}
                error="This code is no longer valid"
              />
            </YStack>

            {/* type="url" — no autocapitalize/autocorrect/spellcheck (every
              one of those mangles a pasted link); three real call sites
              share this exact type + `normalizeSocialLink` on blur:
              profile/edit.tsx's social-link field, SocialLinkSheet (the
              approval-gate sheet), and memberships/purchase/status.tsx's
              share-your-pass field. Type a non-link value and blur to see
              the rejection. */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                type=&quot;url&quot; — no autocapitalize/autocorrect; validated
                with `normalizeSocialLink` on blur (profile/edit.tsx,
                SocialLinkSheet, purchase/status.tsx)
              </Text>
              <PSInput
                type="url"
                value={urlInput}
                onChangeText={(next) => {
                  setUrlInput(next);
                  setUrlError(undefined);
                }}
                placeholder="https://instagram.com/you"
                error={urlError}
                onBlur={() =>
                  setUrlError(
                    urlInput.trim() && !normalizeSocialLink(urlInput)
                      ? 'Enter a valid profile link'
                      : undefined
                  )
                }
              />
            </YStack>

            {/* disabled — dimmed (opacity 0.4), not editable. delete-account/
              confirm.tsx's confirm-phrase field is the real call site: it
              also passes no `type` at all (defaults to "text"), and flips
              `disabled` on only WHILE the delete request is in flight — the
              same instant PSButton's own destructive/loading demo covers on
              the ps-button page, this is the input half of that moment. */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                disabled — dimmed, not editable (delete-account/confirm.tsx,
                while the delete request is in flight)
              </Text>
              <PSInput
                value="Delete my account"
                onChangeText={() => {}}
                disabled
              />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
